import React, { useState, useEffect, useRef } from 'react';

import { useMediaQuery } from 'hooks/useMediaQuery';

// Marquee speed for the overflowing placeholder, in px/s.
const PLACEHOLDER_MARQUEE_SPEED = 40;

// Fake placeholder overlay for a search input (T2B-390) — the native
// placeholder, which clips silently, stays empty. On mobile, when the text
// overflows, it turns into a continuous ticker-style marquee of all prompts,
// matching TickerSection: content duplicated, translateX 0 → -50%, linear,
// infinite. On larger screens it always shows the current prompt statically —
// the marquee is a mobile-only behavior.
export default function ScrollingPlaceholder({ text, items }) {
  const boxRef = useRef(null);
  const measureRef = useRef(null);
  const trackRef = useRef(null);
  const [marquee, setMarquee] = useState(false);
  const [duration, setDuration] = useState(0);

  const isMobile = useMediaQuery('(max-width: 640px)');

  const list = items?.length ? items : [text];
  const longest = list.reduce((a, b) => (b.length > a.length ? b : a), '');
  // Non-breaking spaces so the separators survive whitespace collapsing.
  const loopText = `${list.join('  ·  ')}  ·  `;

  useEffect(() => {
    const box = boxRef.current;
    const meas = measureRef.current;
    if (!box || !meas) return undefined;
    const check = () =>
      setMarquee(isMobile && meas.scrollWidth > box.clientWidth + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(box);
    return () => ro.disconnect();
  }, [longest, isMobile]);

  useEffect(() => {
    if (!marquee || !trackRef.current) return;
    const halfWidth = trackRef.current.scrollWidth / 2;
    if (halfWidth > 0) setDuration(halfWidth / PLACEHOLDER_MARQUEE_SPEED);
  }, [marquee, loopText]);

  return (
    <div
      ref={boxRef}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 flex items-center overflow-hidden"
    >
      <span
        ref={measureRef}
        className="invisible absolute whitespace-nowrap text-[14px]"
      >
        {longest}
      </span>
      {marquee ? (
        <div
          ref={trackRef}
          className="placeholder-marquee flex w-max whitespace-nowrap text-[14px] text-gray-400 will-change-transform"
          style={{ '--marquee-duration': `${duration || 20}s` }}
        >
          <span>{loopText}</span>
          <span>{loopText}</span>
        </div>
      ) : (
        <span className="whitespace-nowrap text-[14px] text-gray-400 truncate">
          {text}
        </span>
      )}
    </div>
  );
}
